/**
 * The formatting toolbar — V7-04 §7.
 *
 * Six buttons and nothing else: bold, italic, code, code block, bullet list,
 * numbered list, link. §7 is explicit that this extends the existing limited
 * markdown rather than becoming a document editor, so every button writes the
 * characters a person could have typed, and a message is the same message
 * whichever way it was written.
 */

import { Bold, Code, Italic, Link2, List, ListOrdered, SquareCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Format } from './composer';

const BUTTONS: { format: Format; label: string; icon: React.ReactNode }[] = [
  { format: 'bold', label: 'Bold', icon: <Bold /> },
  { format: 'italic', label: 'Italic', icon: <Italic /> },
  { format: 'code', label: 'Code', icon: <Code /> },
  { format: 'codeBlock', label: 'Code block', icon: <SquareCode /> },
  { format: 'bullet', label: 'Bulleted list', icon: <List /> },
  { format: 'numbered', label: 'Numbered list', icon: <ListOrdered /> },
  { format: 'link', label: 'Link', icon: <Link2 /> },
];

export function ComposerToolbar({ onFormat }: { onFormat: (format: Format) => void }) {
  return (
    <div className="flex items-center gap-0.5" data-testid="composer-toolbar">
      {BUTTONS.map(({ format, label, icon }) => (
        <Button
          key={format}
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          title={label}
          data-testid={`composer-${format}`}
          // The composer must keep focus and its selection: a toolbar that
          // steals focus formats nothing, because there is no selection left.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onFormat(format)}
        >
          {icon}
        </Button>
      ))}
    </div>
  );
}
